import { useClientTranslation } from '@lib/i18n/client';
import { TariffCard } from '@modules/get-slash-internet-card/components/dialog-views/booking-view/tariff-card';
import { TariffDetails } from '@modules/get-slash-internet-card/components/dialog-views/booking-view/tariff-details';
import { DialogBody } from '@modules/get-slash-internet-card/components/dialog-views/dialog-body';
import { useViewStack } from '@modules/get-slash-internet-card/components/dialog-views/view-stack';
import { ViewStackViewWithNavigation } from '@modules/get-slash-internet-card/components/dialog-views/view-stack-view-with-navigation';
import { combineTariffs } from '@modules/get-slash-internet-card/lib/combine-tariffs';
import { useBookableTariffs } from '@modules/get-slash-internet-card/lib/use-bookable-tariffs';
import { getPriceExpression } from '@modules/get-slash-internet-card/lib/utils';
import { useMemo } from 'react';

import type { GetSlashCredentials } from '@lib/third-parties/get-slash.types';
import type { RefreshDialogHandler } from '@modules/get-slash-internet-card/components/dialog-view-renderer';
import type { CombinedInternetTariff } from '@modules/get-slash-internet-card/lib/combine-tariffs';
import type { GetInternetDataTariff } from '@modules/get-slash-internet-card/lib/internet-card-view-data';
import type { Locale } from '@tuic/i18n';
import type { ComponentPropsWithoutRef, FC } from 'react';

export type { CombinedInternetTariff } from '@modules/get-slash-internet-card/lib/combine-tariffs';

type Props = {
  locale: Locale;
  tariffs?: GetInternetDataTariff[];
  fallbackImages?: Pick<
    GetInternetDataTariff,
    'imageListing' | 'imageHero' | 'imageHeroMd'
  >;
  getSlashCredentials: GetSlashCredentials;
  useDebugEndpoints?: boolean;
  refreshDialog: RefreshDialogHandler;
  termsOfUseCheckbox: ComponentPropsWithoutRef<
    typeof TariffDetails
  >['termsOfUseCheckbox'];
};

export const TariffListing: FC<Props> = props => {
  const {
    locale,
    tariffs = [],
    fallbackImages,
    getSlashCredentials,
    useDebugEndpoints,
    refreshDialog,
    termsOfUseCheckbox,
  } = props;

  const { push } = useViewStack();

  const { t } = useClientTranslation();

  const bookableTariffs = useBookableTariffs({
    credentials: getSlashCredentials,
    useDebugEndpoints,
  });

  const { tariffs: getSlashTariffs, ready } = bookableTariffs;

  const combinedTariffs = useMemo(() => {
    if (!ready) {
      return [];
    }

    return combineTariffs(getSlashTariffs, tariffs, fallbackImages);
  }, [getSlashTariffs, tariffs, fallbackImages, ready]);

  const handleBuy = async (tariff: CombinedInternetTariff) => {
    const response = await bookableTariffs.book(tariff.externalId);

    if (response) {
      return true;
    }

    return t(
      'Bitte versuchen Sie es später erneut. Sollte der Fehler bestehen bleiben, wenden Sie sich bitte an das Mein Schiff ® Service Team.',
    );
  };

  const showTariffDetails = (tariff: CombinedInternetTariff) => {
    push(
      <TariffDetails
        locale={locale}
        tariff={tariff}
        buyTariff={handleBuy}
        refreshDialog={refreshDialog}
        termsOfUseCheckbox={termsOfUseCheckbox}
      />,
    );
  };

  return (
    <DialogBody colored>
      <ViewStackViewWithNavigation title={t('Alle Internetpakete')}>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {!ready && (
            <>
              {Array(4)
                .fill(null)
                .map((_, idx) => (
                  <li key={idx}>
                    <TariffCard fallback />
                  </li>
                ))}
            </>
          )}

          {ready && (
            <>
              {combinedTariffs.map(tariff => (
                <li key={tariff.externalId}>
                  <TariffCard
                    image={tariff.imageListing}
                    headline={tariff.title ?? tariff.getSlash.name}
                    text={tariff.teaser}
                    priceExpression={getPriceExpression(locale, tariff)}
                    tag={tariff.tag}
                    asChild
                  >
                    <button onClick={() => showTariffDetails(tariff)} />
                  </TariffCard>
                </li>
              ))}
            </>
          )}
        </ul>
      </ViewStackViewWithNavigation>
    </DialogBody>
  );
};
